import { ArrowLeft, TrendingUp } from 'lucide-react';
import AnalyticsInfoHint from './AnalyticsInfoHint';

/**
 * Layout page détail Analytics — même philosophie visuelle que OverviewStatDetail
 * (hero gradient, retour, sous-KPI, sections). Ne modifie pas Vue d’ensemble.
 */
export default function AnalyticsDetailLayout({
  title,
  subtitle,
  merchantName,
  definition,
  periodLabel,
  mainValue,
  mainSuffix = '',
  deltaLabel,
  gradient = 'from-slate-600 to-slate-800',
  icon: Icon,
  onBack,
  backLabel = 'Retour Analytics',
  understand,
  lead,
  evolution,
  detail,
  actionSoon,
  secondaryAction,
  periodControls,
  children,
}) {
  return (
    <div className="space-y-6" data-testid="analytics-detail-page">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <button
          type="button"
          onClick={onBack}
          className="inline-flex items-center text-sm text-gray-600 hover:text-gray-900 shrink-0"
          data-testid="analytics-detail-back"
        >
          <ArrowLeft className="h-4 w-4 mr-2" aria-hidden />
          {backLabel}
        </button>
        <div className="flex flex-col items-stretch sm:items-end gap-2 min-w-0 flex-1">
          {periodControls || null}
          {secondaryAction || null}
        </div>
      </div>

      <div className={`rounded-2xl p-6 text-white bg-gradient-to-r ${gradient} shadow-lg`}>
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-4 min-w-0">
            {Icon ? (
              <div className="p-3 rounded-xl bg-white/20 backdrop-blur shrink-0">
                <Icon className="h-10 w-10" aria-hidden />
              </div>
            ) : null}
            <div className="min-w-0">
              <h2 className="text-2xl font-bold flex items-center gap-2 flex-wrap">
                {title}
                <span className="inline-flex text-white/90">
                  <AnalyticsInfoHint text={definition} label={`Définition ${title}`} />
                </span>
              </h2>
              <p className="text-sm text-white/90 flex items-center gap-1 mt-1 flex-wrap">
                <TrendingUp className="h-3.5 w-3.5 shrink-0" aria-hidden />
                {subtitle || 'Analyse approfondie'}
                {merchantName ? ` — ${merchantName}` : ''}
              </p>
              {periodLabel ? (
                <p className="text-xs text-white/80 mt-1">Période : {periodLabel}</p>
              ) : null}
            </div>
          </div>
          <div className="rounded-xl bg-white/15 backdrop-blur px-5 py-3 min-w-[9rem]">
            <p className="text-[11px] uppercase tracking-wide text-white/80">Valeur</p>
            <p className="text-3xl font-bold tracking-tight">
              {mainValue}
              {mainSuffix ? <span className="text-lg font-semibold text-white/80">{mainSuffix}</span> : null}
            </p>
            {deltaLabel ? <p className="mt-0.5 text-xs text-white/75">{deltaLabel}</p> : null}
          </div>
        </div>
      </div>

      {understand ? (
        <section className="rounded-xl border border-blue-100 bg-blue-50/80 p-5 shadow-sm">
          <h2 className="text-sm font-semibold text-blue-950 mb-2">
            {understand.sectionTitle || 'Ce qui est pris en compte'}
          </h2>
          {understand.summary ? (
            <p className="text-sm text-blue-900/90 mb-3">{understand.summary}</p>
          ) : null}
          {understand.method?.length ? (
            <div className="mb-3 rounded-lg border border-blue-100 bg-white/80 px-3 py-3 space-y-1.5">
              <p className="text-xs font-semibold text-blue-950 uppercase tracking-wide">
                Méthode de calcul
              </p>
              {understand.method.map((line) => (
                <p key={line.label} className="text-sm text-blue-900 flex flex-wrap gap-x-2">
                  <span className="text-blue-800/80">{line.label}</span>
                  {line.value != null ? (
                    <span className="font-semibold text-blue-950">{line.value}</span>
                  ) : null}
                </p>
              ))}
            </div>
          ) : null}
          {(understand.rows || []).length > 0 ? (
            <dl className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {understand.rows.map((row) => (
                <div
                  key={row.label}
                  className="rounded-xl border border-blue-100 bg-gradient-to-br from-white to-blue-50/50 px-3 py-2.5 shadow-sm"
                >
                  <dt className="text-xs font-medium text-blue-800/80 uppercase tracking-wide flex items-center gap-1">
                    {row.label}
                    {row.hint ? <AnalyticsInfoHint text={row.hint} /> : null}
                  </dt>
                  <dd className="text-xl font-bold text-blue-950 mt-1">{row.value}</dd>
                </div>
              ))}
            </dl>
          ) : null}
        </section>
      ) : null}

      {lead || null}

      {evolution ? (
        <section
          className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm"
          data-testid="analytics-evolution-section"
        >
          <h2 className="text-sm font-semibold text-gray-900 uppercase tracking-wide mb-3">
            Évolution
          </h2>
          {evolution}
        </section>
      ) : null}

      {detail ? (
        <section className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
          <h2 className="text-sm font-semibold text-gray-900 uppercase tracking-wide mb-3">
            {detail.title || 'Détail opérationnel'}
          </h2>
          {detail.body}
        </section>
      ) : null}

      {children}

      {actionSoon ? (
        <p className="text-[11px] text-gray-400 border-t border-gray-100 pt-3">
          Action avancée — À venir
          {actionSoon !== true && actionSoon !== 'À venir' ? ` · ${actionSoon}` : ''}
        </p>
      ) : null}
    </div>
  );
}
